import { createFileRoute, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { CountrySelect } from "@/components/auth/AccountFields";
import { countryName } from "@/lib/countries";
import { useMembership } from "@/hooks/useMembership";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AvatarUploader } from "@/components/AvatarUploader";
import { ShareFeedbackCard } from "@/components/feedback/ShareFeedbackCard";
import { AvatarUploaderBoundary } from "@/components/AvatarUploaderBoundary";
import { getAdminOverview } from "@/lib/admin.functions";
import { saveProfileDetails } from "@/lib/profile.functions";
import { signOutOtherDevices } from "@/lib/devices.functions";
import { getDeviceId } from "@/lib/device-id";
import {
  uploadAvatar,
  AVATAR_MAX_BYTES,
  AVATAR_ALLOWED_TYPES,
} from "@/lib/avatar-upload.functions";
import { getArticlesReadCount } from "@/lib/article-reads";
import { subscribeActivityChange } from "@/lib/activity-events";
import { buildStreakTooltip, computeStreak, formatStreakRange, streakTimezoneFallbackHint, type StreakRow } from "@/lib/streak";
import {
  validateProfile,
  type ProfileFieldErrors,
} from "@/lib/profile-schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import {
  UserRound,
  Mail,
  MapPin,
  
  Clock,
  Camera,
  BadgeCheck,
  CalendarDays,
  Heart,
  CalendarRange,
  SlidersHorizontal,
  Sparkles,
  BookOpen,
  Activity,
  Smile,
  Flame,
  Info,
  Lock,
  ShieldCheck,
  ShieldAlert,
  Bell,
  Moon,
  Sun,
  Eye,
  EyeOff,
  Languages,
  ArrowRight,
  RefreshCw,
  Shield,
  Users,
  CreditCard,
  FileText,
  MessageSquare,
  BarChart3,
  Loader2,
  Save,
  Trash2,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const Route = createFileRoute("/profile")({
  head: () => ({
    meta: [
      { title: "My Profile — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Manage your personal information and customise your Recovery Trauma experience.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: ProfilePage,
});

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "nl", label: "Nederlands" },
  { code: "pl", label: "Polski" },
];

type ProfileForm = {
  first_name: string;
  last_name: string;
  display_name: string;
  country: string;
  region_state: string;
  time_zone: string;
  preferred_language: string;
  date_of_birth: string;
  daily_reminder_time: string;
  bio: string;
  newsletter_opt_in: boolean;
  profile_visibility: string;
  anonymous_mode: boolean;
};

const EMPTY_FORM: ProfileForm = {
  first_name: "",
  last_name: "",
  display_name: "",
  country: "",
  region_state: "",
  time_zone: "",
  preferred_language: "en",
  date_of_birth: "",
  daily_reminder_time: "",
  bio: "",
  newsletter_opt_in: true,
  profile_visibility: "private",
  anonymous_mode: false,
};

function ProfilePage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const membershipStatus = useMembership();
  const [tier, setTier] = useState<string>("free");
  const [joinedAt, setJoinedAt] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<ProfileForm>(EMPTY_FORM);
  const [initialForm, setInitialForm] = useState<ProfileForm>(EMPTY_FORM);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [removeConfirming, setRemoveConfirming] = useState(false);
  /** Kept in memory after a failed upload so the user can retry without
   * re-picking the file. Cleared on success or explicit dismiss. */
  const [failedUpload, setFailedUpload] = useState<
    { blob: Blob; attempts: number } | null
  >(null);
  const [saveState, setSaveState] = useState<"idle" | "saving">("idle");
  // Editing is a member benefit — an unpaid account can look, not change.
  const [canEdit, setCanEdit] = useState(true);
  const [errors, setErrors] = useState<ProfileFieldErrors>({});
  const saveProfileFn = useServerFn(saveProfileDetails);
  const [activity, setActivity] = useState({
    journal: 0,
    dreams: 0,
    moods: 0,
    sessions: 0,
    favourites: 0,
    events: 0,
    articles: 0,
    streak: 0,
    streakRange: null as { start: string; end: string } | null,
    streakTimeZone: null as string | null,
    streakTimeZoneFallback: false,
  });
  const [activityState, setActivityState] = useState<"loading" | "ready" | "error">("loading");
  // Track URL hash so we can scroll to the right section on both cold loads
  // (once loading finishes and the section actually exists in the DOM) and
  // in-app navigations from the account menu.
  const routeHash = useRouterState({ select: (s) => s.location.hash });
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    if (typeof document === "undefined") return "light";
    return document.documentElement.classList.contains("dark") ? "dark" : "light";
  });
  const [delState, setDelState] = useState<"idle" | "sending" | "sent">("idle");
  const [delOpen, setDelOpen] = useState(false);
  const [delConfirmText, setDelConfirmText] = useState("");
  const [pwOpen, setPwOpen] = useState(false);
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwShow, setPwShow] = useState(false);
  const [pwSubmitting, setPwSubmitting] = useState(false);

  const loadActivity = useCallback(async (uid: string) => {
    setActivityState("loading");
    try {
      const nowIso = new Date().toISOString();
      const [
        j,
        m,
        s,
        d,
        favCount,
        eventsAttended,
        journalDates,
        dreamDates,
        moodDates,
        sessionDates,
      ] = await Promise.all([
        supabase
          .from("journal_entries")
          .select("id", { count: "exact", head: true })
          .eq("user_id", uid),
        supabase
          .from("mood_checks")
          .select("id", { count: "exact", head: true })
          .eq("user_id", uid),
        supabase
          .from("exercise_sessions")
          .select("id", { count: "exact", head: true })
          .eq("user_id", uid),
        supabase
          .from("dreams")
          .select("id", { count: "exact", head: true })
          .eq("user_id", uid),
        (async () => {
          try {
            let total = 0;
            for (let i = 0; i < localStorage.length; i++) {
              const k = localStorage.key(i);
              if (!k || !k.startsWith("rt:fav-")) continue;
              const raw = localStorage.getItem(k);
              if (!raw) continue;
              const arr = JSON.parse(raw);
              if (Array.isArray(arr)) total += arr.length;
            }
            return total;
          } catch {
            return 0;
          }
        })(),
        (async () => {
          const { data: rows } = await supabase
            .from("event_reservations")
            .select("event_id, events!inner(starts_at, duration_minutes)")
            .eq("user_id", uid);
          if (!rows) return 0;
          return rows.reduce((n, r: { events: { starts_at: string; duration_minutes: number } | null }) => {
            const ev = r.events;
            if (!ev) return n;
            const end = new Date(ev.starts_at).getTime() + (ev.duration_minutes ?? 0) * 60_000;
            return end < Date.now() ? n + 1 : n;
          }, 0);
        })(),
        supabase.from("journal_entries").select("created_at").eq("user_id", uid).lte("created_at", nowIso),
        supabase.from("dreams").select("created_at").eq("user_id", uid).lte("created_at", nowIso),
        supabase.from("mood_checks").select("created_at").eq("user_id", uid).lte("created_at", nowIso),
        supabase.from("exercise_sessions").select("started_at").eq("user_id", uid).lte("started_at", nowIso),
      ]);

      // Resolve a time zone for day bucketing: prefer the saved profile tz,
      // fall back to the device's resolved tz, then UTC. Probe each candidate
      // through Intl so a stale/typo value doesn't throw RangeError.
      let browserTz = "UTC";
      try {
        if (typeof Intl !== "undefined") {
          browserTz =
            Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";
        }
      } catch {
        browserTz = "UTC";
      }
      const rawProfileTz =
        typeof form.time_zone === "string" ? form.time_zone.trim() : "";
      const candidateTz = rawProfileTz || browserTz || "UTC";

      const rows: StreakRow[] = [
        ...((journalDates.data as StreakRow[] | null) ?? []),
        ...((dreamDates.data as StreakRow[] | null) ?? []),
        ...((moodDates.data as StreakRow[] | null) ?? []),
        ...((sessionDates.data as StreakRow[] | null) ?? []),
      ];
      const {
        streak,
        streakRange,
        streakTimeZone,
        streakTimeZoneFallback,
      } = computeStreak(rows, { timeZone: candidateTz });

      setActivity({
        journal: j.count ?? 0,
        dreams: d.count ?? 0,
        moods: m.count ?? 0,
        sessions: s.count ?? 0,
        favourites: favCount,
        events: eventsAttended,
        articles: getArticlesReadCount(),
        streak,
        streakRange,
        streakTimeZone,
        streakTimeZoneFallback,
      });
      setActivityState("ready");
    } catch (err) {
      console.error("Failed to load Recovery Journey stats", err);
      setActivityState("error");
    }
  }, [form.time_zone]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: authError } = await supabase.auth.getUser();
        if (!data.user) {
          // Never treat a failed/slow lookup as "signed out" — only redirect
          // when there is definitively no session.
          const { readStoredAuthSession } = await import("@/lib/auth-session");
          if (authError && readStoredAuthSession()?.user?.id) return;
          navigate({ to: "/login", search: { redirect: "/profile" } });
          return;
        }
        if (cancelled) return;
        setEmail(data.user.email ?? null);
        setUserId(data.user.id);
        const [profileRes, roleRes] = await Promise.all([
          supabase
            .from("profiles")
            .select(
              "membership_tier, joined_at, avatar_url, display_name, first_name, last_name, country, region_state, time_zone, preferred_language, date_of_birth, daily_reminder_time, bio, newsletter_opt_in, profile_visibility, anonymous_mode",
            )
            .eq("user_id", data.user.id)
            .maybeSingle(),
          supabase.rpc("has_role", { _user_id: data.user.id, _role: "admin" }),
        ]);
        const accessRes = await supabase.rpc("has_platform_access", {
          _user_id: data.user.id,
        });
        if (!cancelled) setCanEdit(accessRes.error ? false : accessRes.data === true);
        if (cancelled) return;
        const profile = profileRes.data;
        setTier(profile?.membership_tier ?? "free");
        setJoinedAt(profile?.joined_at ?? null);
        setAvatarUrl(profile?.avatar_url ?? null);
        setIsAdmin(!roleRes.error && !!roleRes.data);

        const next: ProfileForm = {
          first_name: profile?.first_name ?? "",
          last_name: profile?.last_name ?? "",
          display_name: profile?.display_name ?? "",
          country: profile?.country ?? "",
          region_state: profile?.region_state ?? "",
          time_zone:
            profile?.time_zone ??
            (typeof Intl !== "undefined"
              ? Intl.DateTimeFormat().resolvedOptions().timeZone
              : ""),
          preferred_language: profile?.preferred_language ?? "en",
          date_of_birth: profile?.date_of_birth ?? "",
          daily_reminder_time: profile?.daily_reminder_time ?? "",
          bio: profile?.bio ?? "",
          newsletter_opt_in: profile?.newsletter_opt_in ?? true,
          profile_visibility: profile?.profile_visibility ?? "private",
          anonymous_mode: profile?.anonymous_mode ?? false,
        };
        setForm(next);
        setInitialForm(next);
        void loadActivity(data.user.id);
      } catch (err) {
        console.error("Profile load failed", err);
        toast.error("We couldn't load your profile. Please refresh and try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, loadActivity]);

  // Scroll to the hashed section once the target actually exists in the DOM.
  // Handles both cold loads (target only mounts after `loading` flips) and
  // in-app navigations that just change the hash (browsers don't re-scroll
  // for a same-page hash change during a client-side route update).
  useEffect(() => {
    if (loading) return;
    const raw = routeHash?.replace(/^#/, "");
    if (!raw) return;
    // Two RAFs so layout has settled before we measure.
    let cancelled = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        const el = document.getElementById(raw);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
    return () => {
      cancelled = true;
    };
  }, [routeHash, loading]);



  // Live refresh: when the member records a new journal/dream/mood, marks an
  // article read, or reserves an event from anywhere in the app, recompute the
  // Recovery Journey stats without a page reload. Debounced so a burst of
  // events (e.g. autosave + complete) only triggers one fetch, and also fires
  // when the tab regains focus to catch updates made in another tab.
  useEffect(() => {
    if (!userId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        void loadActivity(userId);
      }, 250);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") schedule();
    };
    const unsubscribe = subscribeActivityChange(schedule);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [userId, loadActivity]);


  const changedFields = useMemo(() => {
    const keys = Object.keys(form) as (keyof ProfileForm)[];
    const norm = (v: unknown) =>
      typeof v === "string" ? v.trim() : v ?? "";
    return keys.filter((k) => norm(form[k]) !== norm(initialForm[k]));
  }, [form, initialForm]);
  const dirty = changedFields.length > 0;
  const unsavedLabel =
    changedFields.length === 1
      ? "1 unsaved change"
      : `${changedFields.length} unsaved changes`;

  function validateField<K extends keyof typeof form>(key: K) {
    const result = validateProfile(form);
    setErrors((prev) => {
      const next = { ...prev };
      if (result.ok) {
        delete next[key as keyof ProfileFieldErrors];
      } else if (result.errors[key as keyof ProfileFieldErrors]) {
        next[key as keyof ProfileFieldErrors] =
          result.errors[key as keyof ProfileFieldErrors];
      } else {
        delete next[key as keyof ProfileFieldErrors];
      }
      return next;
    });
  }

  async function saveAll() {
    if (!userId) return;
    if (!canEdit) {
      toast.error("Your membership isn't active yet", {
        description:
          "You can update your profile once your membership is active. Nothing you've written is lost.",
      });
      return;
    }

    // Client-side validation first — fast feedback, no network round-trip.
    const local = validateProfile(form);
    if (!local.ok) {
      setErrors(local.errors);
      toast.error("Please check your profile details", {
        description:
          "A few fields need a small adjustment before we can save your profile.",
      });
      // Focus the first invalid field for screen readers.
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>('[aria-invalid="true"]');
        el?.focus();
      });
      return;
    }
    setErrors({});

    setSaveState("saving");
    try {
      const result = await saveProfileFn({ data: local.data });
      if (!result.ok) {
        setErrors(result.errors ?? {});
        toast.error("We couldn't save your profile", {
          description: result.message,
        });
        return;
      }
      setInitialForm(form);
      toast.success("Profile saved", {
        description: "Your changes have been updated successfully.",
      });
    } catch (err) {
      console.error("[profile] save failed", err);
      toast.error("Couldn't reach the server", {
        description: "Please check your connection and try again in a moment.",
      });
    } finally {
      setSaveState("idle");
    }
  }

  function cancelEdits() {
    setForm(initialForm);
    setErrors({});
  }


  function toggleTheme() {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    try {
      document.documentElement.classList.toggle("dark", next === "dark");
      localStorage.setItem("rt:theme", next);
    } catch {}
  }

  async function uploadAvatarBlob(blob: Blob): Promise<boolean> {
    if (!userId) return false;
    if (!canEdit) {
      toast.error("Your membership isn't active yet", {
        description: "You can change your photo once your membership is active.",
      });
      return false;
    }

    // Client-side pre-check — same rules the server enforces. This gives fast
    // feedback but is NOT the source of truth; the server re-validates.
    const contentType = (AVATAR_ALLOWED_TYPES as readonly string[]).includes(blob.type)
      ? blob.type
      : "image/jpeg";
    if (!(AVATAR_ALLOWED_TYPES as readonly string[]).includes(contentType)) {
      toast.error("Unsupported image type", {
        description: "Please choose a JPEG, PNG, or WebP image.",
      });
      return false;
    }
    if (blob.size > AVATAR_MAX_BYTES) {
      toast.error("Image is too large", {
        description: "Maximum size is 5 MB. Please choose a smaller photo.",
      });
      return false;
    }

    setAvatarUploading(true);
    const localUrl = URL.createObjectURL(blob);
    const previousUrl = avatarUrl;
    setAvatarUrl(localUrl);
    const priorAttempts = failedUpload?.blob === blob ? failedUpload.attempts : 0;
    try {
      // Encode as base64 data URL for the server function payload.
      const buf = await blob.arrayBuffer();
      let binary = "";
      const bytes = new Uint8Array(buf);
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
      }
      const b64 = btoa(binary);
      const dataUrl = `data:${contentType};base64,${b64}`;

      const { url } = await uploadAvatar({
        data: { dataUrl, contentType, byteLength: blob.size },
      });

      URL.revokeObjectURL(localUrl);
      setAvatarUrl(url);
      try {
        localStorage.setItem("rt:avatar-fallback", url);
      } catch {}
      setFailedUpload(null);
      toast.success("Profile photo updated", {
        description: "Your new picture is now visible across Recovery Trauma™.",
      });
      return true;
    } catch (err) {
      console.error("[avatar] upload failed", err);
      try {
        localStorage.setItem("rt:avatar-local", localUrl);
      } catch {}
      if (!localUrl) setAvatarUrl(previousUrl);
      setFailedUpload({ blob, attempts: priorAttempts + 1 });
      const message = err instanceof Error ? err.message : "";
      toast.error("We couldn't save that photo", {
        description: message
          ? `${message} Tap Retry upload to try again.`
          : "We've kept a preview here for you — tap Retry upload to try again.",
      });
      return false;
    } finally {
      setAvatarUploading(false);
    }
  }


  async function retryFailedUpload() {
    if (!failedUpload || avatarUploading) return;
    await uploadAvatarBlob(failedUpload.blob);
  }

  function dismissFailedUpload() {
    setFailedUpload(null);
  }


  async function removeAvatar(): Promise<boolean> {
    if (!userId) return false;
    const previousUrl = avatarUrl;
    setAvatarUploading(true);
    // Optimistically clear so the default avatar shows immediately.
    setAvatarUrl(null);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ avatar_url: null })
        .eq("user_id", userId);
      if (error) throw error;
      // Clear any cached fallbacks so a stale photo doesn't come back.
      try {
        localStorage.removeItem("rt:avatar-fallback");
        localStorage.removeItem("rt:avatar-local");
      } catch {}
      toast.success("Profile photo removed", {
        description: "You're back to the default avatar.",
      });
      setRemoveConfirming(false);
      return true;
    } catch (err) {
      console.error("[avatar] remove failed", err);
      // Roll back the optimistic update so the user isn't misled.
      setAvatarUrl(previousUrl);
      toast.error("We couldn't remove that photo", {
        description: "Please check your connection and try again in a moment.",
      });
      return false;
    } finally {
      setAvatarUploading(false);
    }
  }



  async function changePassword() {
    if (pwNew.length < 8) {
      toast.error("Please choose a password with at least 8 characters.");
      return;
    }
    if (pwNew !== pwConfirm) {
      toast.error("Both passwords need to match.");
      return;
    }
    setPwSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password: pwNew });
    setPwSubmitting(false);
    if (error) {
      toast.error(error.message || "We couldn't update your password.");
      return;
    }
    // A new password signs out every other device, so anyone using the old
    // one loses access straight away. This device stays signed in.
    try {
      await signOutOtherDevices({
        data: { keepDeviceId: getDeviceId(), reason: "password_changed" },
      });
      toast.success("Password updated. All other devices have been signed out.");
    } catch {
      toast.success("Password updated.");
    }
    setPwNew("");
    setPwConfirm("");
    setPwOpen(false);
  }




  async function submitDeletionRequest() {
    if (!userId) return;
    setDelState("sending");
    const { error } = await supabase.from("support_tickets").insert({
      user_id: userId,
      user_email: email,
      category: "other",
      subject: "Account & data deletion request (GDPR/CCPA)",
    });
    if (error) {
      console.error(error);
      toast.error("We couldn't submit the request. Please email support.");
      setDelState("idle");
      return;
    }
    setDelState("sent");
    setDelOpen(false);
    setDelConfirmText("");
    toast.success("Deletion request received. We'll be in touch.");
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-sm text-muted-foreground">
        <Loader2 className="me-2 h-4 w-4 animate-spin" /> Loading your profile…
      </div>
    );
  }

  const fullName =
    [form.first_name, form.last_name].filter(Boolean).join(" ").trim() ||
    form.display_name ||
    email?.split("@")[0] ||
    "Friend";

  // Complimentary and admin-granted access are real access, so the badge
  // follows actual platform access rather than the paid tier alone.
  const tierLabel = isAdmin
    ? "Admin"
    : tier === "founding" || tier === "healing" || membershipStatus.isMember
      ? "Member"
      : "Free Account";

  return (
    <div className="mx-auto max-w-4xl px-4 pb-8 pt-10 md:pt-14">
      <header className="mb-8 text-center md:text-start">
        <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          My Profile
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
          Manage your personal information and customise your Recovery Trauma™
          experience.
        </p>
      </header>

      {!canEdit ? (
        <div
          role="status"
          className="mb-8 rounded-2xl border border-gold/40 bg-cream/60 px-4 py-4 text-sm leading-relaxed text-foreground/85 dark:bg-cream/10"
        >
          <p className="font-medium">Your profile is read-only for now.</p>
          <p className="mt-1 text-muted-foreground">
            Profile changes are part of membership. Everything you've saved stays
            exactly as it is, and you can edit again as soon as your membership is
            active.
          </p>
          <Link
            to="/complete-membership"
            className="btn-gold mt-3 inline-flex text-sm"
          >
            Activate my membership
          </Link>
        </div>
      ) : null}

      {/* Profile Card */}
      <SectionCard id="profile-photo">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
          <div className="relative mx-auto sm:mx-0">
            <div
              className={`relative flex h-24 w-24 items-center justify-center overflow-hidden rounded-full bg-gold-light/40 ring-2 ring-gold/40 ${avatarUploading ? "ring-[var(--gold-deep)]" : ""}`}
              aria-busy={avatarUploading}
            >
              {avatarUrl ? (
                <img loading="lazy" decoding="async" src={avatarUrl} alt="Your profile picture" className="h-full w-full object-cover" />
              ) : (
                <UserRound
                  className="h-10 w-10 text-[var(--gold-deep)]"
                  strokeWidth={1.5}
                />
              )}
              {avatarUploading ? (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center gap-1 rounded-full bg-black/40 text-white backdrop-blur-[1px]"
                  role="status"
                  aria-live="polite"
                >
                  <Loader2 className="h-6 w-6 animate-spin" strokeWidth={1.75} />
                  <span className="text-[10px] font-medium uppercase tracking-wide">
                    Uploading
                  </span>
                </div>
              ) : null}
            </div>
            {avatarUploading ? (
              <div className="absolute -bottom-3 start-1/2 h-1 w-20 -translate-x-1/2 overflow-hidden rounded-full bg-gold-light/60">
                <div className="h-full w-1/3 animate-[avatarProgress_1.2s_ease-in-out_infinite] rounded-full bg-[var(--gold-deep)]" />
              </div>
            ) : null}
            <label
              className="absolute -bottom-1 -end-1 inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-gold/50 bg-background text-[var(--gold-deep)] shadow-sm transition hover:bg-gold-light/30"
              title="Change photo"
            >
              {avatarUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Camera className="h-4 w-4" strokeWidth={1.5} />
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={avatarUploading || !canEdit}
                onChange={(e) => {
                  try {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (!file.type.startsWith("image/")) {
                        toast.error("That file isn't an image", {
                          description: "Please choose a JPG, PNG, or WEBP photo.",
                        });
                      } else if (file.size > 10 * 1024 * 1024) {
                        toast.error("That photo is too large", {
                          description: "Please choose an image under 10 MB.",
                        });
                      } else if (typeof FileReader === "undefined") {
                        // Very old browser — bypass the cropper entirely and
                        // upload the original file so the flow still works.
                        toast("Uploading your original photo", {
                          description:
                            "Your browser can't preview photos here, so we'll upload it as-is.",
                        });
                        void uploadAvatarBlob(file);
                      } else {
                        setPendingFile(file);
                        setCropperOpen(true);
                      }
                    }
                  } catch (err) {
                    console.error("[avatar] file picker failed", err);
                    toast.error("We couldn't open that photo", {
                      description: "Please try again or pick a different file.",
                    });
                  } finally {
                    // reset so picking the same file again re-triggers change
                    e.target.value = "";
                  }
                }}
              />
            </label>
          </div>
          <div className="min-w-0 flex-1 text-center sm:text-start">
            <p className="font-display text-2xl font-semibold text-foreground">
              {fullName}
            </p>
            <p className="mt-1 inline-flex items-center justify-center gap-1.5 text-sm text-muted-foreground sm:justify-start">
              <Mail className="h-3.5 w-3.5" strokeWidth={1.5} /> {email ?? "—"}
            </p>
            <div className="mt-3 flex flex-wrap justify-center gap-2 sm:justify-start">
              <Chip icon={BadgeCheck}>{tierLabel}</Chip>
              <Chip icon={CalendarDays}>
                Member since{" "}
                {joinedAt
                  ? new Date(joinedAt).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "long",
                    })
                  : "—"}
              </Chip>
              {form.country && (
                <Chip icon={MapPin}>
                  {countryName(form.country)}
                  {form.region_state ? ` · ${form.region_state}` : ""}
                </Chip>
              )}
              {form.time_zone && <Chip icon={Clock}>{form.time_zone}</Chip>}
            </div>
            {avatarUrl ? (
              <div className="mt-3 flex flex-wrap justify-center gap-2 sm:justify-start">
                {removeConfirming ? (
                  <>
                    <button
                      type="button"
                      onClick={() => void removeAvatar()}
                      disabled={avatarUploading}
                      className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-destructive/40 bg-destructive/5 px-3 py-1.5 text-xs font-medium text-destructive transition hover:bg-destructive/10 disabled:opacity-60"
                    >
                      {avatarUploading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      )}
                      Confirm remove
                    </button>
                    <button
                      type="button"
                      onClick={() => setRemoveConfirming(false)}
                      disabled={avatarUploading}
                      className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted disabled:opacity-60"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setRemoveConfirming(true)}
                    disabled={avatarUploading}
                    className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted disabled:opacity-60"
                    aria-label="Remove profile photo"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    Remove photo
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </div>
        {failedUpload ? (
          <div
            className="mt-4 flex flex-col gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-3 sm:flex-row sm:items-center sm:justify-between"
            role="alert"
            aria-live="polite"
          >
            <div className="flex items-start gap-2 text-sm text-destructive">
              <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
              <div>
                <p className="font-medium">Photo didn't upload</p>
                <p className="text-xs text-destructive/80">
                  Your preview is saved here. Retry when you're back online — no
                  need to pick the file again.
                  {failedUpload.attempts > 1
                    ? ` (Attempt ${failedUpload.attempts})`
                    : ""}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => void retryFailedUpload()}
                disabled={avatarUploading}
                className="inline-flex items-center gap-1.5 rounded-full bg-[var(--gold-deep)] px-3 py-1.5 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
              >
                {avatarUploading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />
                )}
                Retry upload
              </button>
              <button
                type="button"
                onClick={dismissFailedUpload}
                disabled={avatarUploading}
                className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted disabled:opacity-60"
              >
                Dismiss
              </button>
            </div>
          </div>
        ) : null}
      </SectionCard>

      {isAdmin && <AdminStatsSection />}

      {/* Personal Information */}
      <SectionCard
        id="personal-info"
        title="Personal information"
        subtitle="Manage your personal details and how your profile appears within Recovery Trauma™."
        icon={UserRound}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="First name"
            value={form.first_name}
            onChange={(v) => setForm({ ...form, first_name: v })}
            onBlur={() => validateField("first_name")}
            error={errors.first_name}
            disabled={!canEdit}
          />
          <Field
            label="Last name"
            value={form.last_name}
            onChange={(v) => setForm({ ...form, last_name: v })}
            onBlur={() => validateField("last_name")}
            error={errors.last_name}
            disabled={!canEdit}
          />
          <Field
            label="Display name (optional – shown in the Community)"
            value={form.display_name}
            onChange={(v) => setForm({ ...form, display_name: v })}
            onBlur={() => validateField("display_name")}
            error={errors.display_name}
            placeholder="Optional"
            disabled={!canEdit}
          />
          <Field label="Email" value={email ?? ""} onChange={() => {}} disabled />
          <Field
            label="Date of birth (optional)"
            type="date"
            value={form.date_of_birth}
            onChange={(v) => setForm({ ...form, date_of_birth: v })}
            onBlur={() => validateField("date_of_birth")}
            error={errors.date_of_birth}
            disabled={!canEdit}
          />
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Country</span>
            <CountrySelect id="profile-country" required={false} value={form.country === "UK" ? "GB" : form.country} onChange={(v) => setForm({ ...form, country: v })} className="w-full rounded-md border border-input bg-background px-3 py-2 text-base" />
          </label>
        </div>

      </SectionCard>

      {/* About Me */}
      <SectionCard
        title="About me"
        subtitle="Only visible if you choose a public profile."
        icon={Sparkles}
      >
        <textarea
          value={form.bio}
          onChange={(e) =>
            setForm({ ...form, bio: e.target.value.slice(0, 250) })
          }
          rows={4}
          maxLength={250}
          disabled={!canEdit}
          className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground transition focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/20"
          placeholder="Tell us a little about yourself (optional)."
        />
        <p className="mt-1.5 text-end text-[11px] text-muted-foreground">
          {form.bio.length}/250
        </p>
      </SectionCard>

      {/* Recovery Preferences */}
      <SectionCard
        title="Recovery preferences"
        subtitle="Personalise how Recovery Trauma™ shows up for you."
        icon={SlidersHorizontal}
      >
        <div className="space-y-3">

          <div className="flex items-center justify-between gap-4 rounded-xl border border-border/60 bg-background px-4 py-3">
            <div className="flex items-start gap-3">
              {theme === "dark" ? (
                <Moon
                  className="mt-0.5 h-5 w-5 text-[var(--gold-deep)]"
                  strokeWidth={1.5}
                />
              ) : (
                <Sun
                  className="mt-0.5 h-5 w-5 text-[var(--gold-deep)]"
                  strokeWidth={1.5}
                />
              )}
              <div>
                <p className="text-sm font-medium text-foreground">Appearance</p>
                <p className="text-xs text-muted-foreground">
                  Choose what feels gentlest on your eyes.
                </p>
              </div>
            </div>
            <div className="inline-flex rounded-full border border-border bg-background p-1">
              {(["light", "dark"] as const).map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    if (theme !== opt) toggleTheme();
                  }}
                  className={`rounded-full px-3 py-1 text-xs font-medium capitalize transition ${
                    theme === opt
                      ? "bg-[var(--gradient-gold)] text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 rounded-xl border border-border/60 bg-background px-4 py-3">
            <div className="flex items-start gap-3">
              <Bell
                className="mt-0.5 h-5 w-5 text-[var(--gold-deep)]"
                strokeWidth={1.5}
              />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Daily reminder time
                </p>
                <p className="text-xs text-muted-foreground">
                  A gentle nudge for your daily check-in.
                </p>
              </div>
            </div>
            <input
              type="time"
              disabled={!canEdit}
              value={form.daily_reminder_time}
              onChange={(e) =>
                setForm({ ...form, daily_reminder_time: e.target.value })
              }
              className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm text-foreground focus:border-gold/60 focus:outline-none"
            />
          </div>

          <Toggle
            icon={Mail}
            label="Weekly email updates"
            desc="A short, calm note each week — opt out anytime."
            value={form.newsletter_opt_in}
            onChange={(v) => setForm({ ...form, newsletter_opt_in: v })}
          />

          {/* Community posts always show the member's display name — no
              anonymous mode and no profile-visibility choice. */}


          <Link
            to="/settings/accessibility"
            className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-background px-4 py-3 transition hover:border-gold/40"
          >
            <div className="flex items-start gap-3">
              <SlidersHorizontal
                className="mt-0.5 h-5 w-5 text-[var(--gold-deep)]"
                strokeWidth={1.5}
              />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Accessibility preferences
                </p>
                <p className="text-xs text-muted-foreground">
                  Text size, contrast, spacing, motion and touch targets.
                </p>
              </div>
            </div>
            <ArrowRight
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </Link>

          <Link
            to="/settings/email-preferences"
            className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-background px-4 py-3 transition hover:border-gold/40"
          >
            <div className="flex items-start gap-3">
              <SlidersHorizontal
                className="mt-0.5 h-5 w-5 text-[var(--gold-deep)]"
                strokeWidth={1.5}
              />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Email preferences
                </p>
                <p className="text-xs text-muted-foreground">
                  Choose which product, membership, event and marketing emails you receive.
                </p>
              </div>
            </div>
            <ArrowRight
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </Link>
        </div>
      </SectionCard>

      {/* Recovery Journey */}
      <SectionCard
        title="Recovery journey"
        subtitle="A gentle reflection of your time here — never a scoreboard."
        icon={Heart}
      >
        {activityState === "loading" ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" aria-busy="true" aria-label="Loading your recovery journey">
            {Array.from({ length: 7 }).map((_, i) => (
              <div
                key={i}
                className="h-[88px] animate-pulse rounded-2xl border border-border/40 bg-muted/40"
              />
            ))}
          </div>
        ) : activityState === "error" ? (
          <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-5 text-center">
            <p className="text-sm text-foreground/80">
              We couldn't load your Recovery Journey right now.
            </p>
            <button
              type="button"
              onClick={() => userId && loadActivity(userId)}
              className="mt-3 inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 px-4 py-1.5 text-xs font-medium text-foreground/80 transition hover:bg-gold/10"
            >
              Try again
            </button>
          </div>
        ) : (
          (() => {
            const total =
              activity.journal +
              activity.dreams +
              activity.moods +
              activity.sessions +
              activity.favourites +
              activity.events +
              activity.articles;
            if (total === 0 && !joinedAt) {
              return (
                <p className="rounded-2xl border border-border/40 bg-muted/30 p-5 text-center text-sm text-muted-foreground">
                  Your journey begins the moment you do. Add a journal entry, save a tool, or join an event to start gathering gentle reflections here.
                </p>
              );
            }
            return (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <JourneyStat
                  icon={CalendarDays}
                  label="Member since"
                  value={
                    joinedAt
                      ? new Date(joinedAt).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                        })
                      : "—"
                  }
                />
                <JourneyStat
                  icon={BookOpen}
                  label="Journal entries"
                  value={activity.journal + activity.dreams}
                  to="/journal"
                  hint={
                    activity.dreams
                      ? `${activity.journal} journal · ${activity.dreams} dream`
                      : undefined
                  }
                />
                <JourneyStat
                  icon={Heart}
                  label="Favourite tools"
                  value={activity.favourites}
                  to="/favorites"
                  iconClassName="text-red-500"
                  iconFill="currentColor"
                />
                <JourneyStat
                  icon={CalendarRange}
                  label="Events attended"
                  value={activity.events}
                  to="/events"
                />
                <JourneyStat
                  icon={BookOpen}
                  label="Articles read"
                  value={activity.articles}
                  to="/library"
                />
                <JourneyStat
                  icon={Smile}
                  label="Mood check-ins"
                  value={activity.moods}
                  to="/mood"
                />
                <JourneyStat
                  icon={Flame}
                  label="Current streak"
                  value={activity.streak ? `${activity.streak} ${activity.streak === 1 ? "day" : "days"}` : "—"}
                  to="/journal"
                  tooltip={buildStreakTooltip(
                    activity.streakTimeZoneFallback ? null : activity.streakTimeZone,
                    { locale: form.preferred_language },
                  )}
                  hint={
                    activity.streak && activity.streakRange
                      ? `${formatStreakRange(
                          activity.streakRange.start,
                          activity.streakRange.end,
                          activity.streakTimeZoneFallback ? null : activity.streakTimeZone,
                          { locale: form.preferred_language },
                        )}${activity.streakTimeZoneFallback ? ` · ${streakTimezoneFallbackHint(form.preferred_language)}` : ""}`
                      : undefined
                  }
                />



              </div>
            );
          })()
        )}
      </SectionCard>

      {/* Quick Access */}
      <SectionCard
        title="Quick access"
        subtitle="Jump back to the parts of your member space."
        icon={Sparkles}
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <QuickLink to="/my-membership" icon={BadgeCheck} label="My Membership" />
          <QuickLink to="/portal" icon={BadgeCheck} label="Member Portal" />
          <QuickLink to="/my-events" icon={CalendarRange} label="My Events" />
          <QuickLink to="/favorites" icon={Heart} label="My Favourites" iconClassName="text-red-500" iconFill="currentColor" />
          <QuickLink to="/preferences" icon={SlidersHorizontal} label="Preferences" />
          <QuickLink to="/account/privacy" icon={ShieldCheck} label="Privacy & data" />
          <QuickLink to="/terms" icon={FileText} label="Terms & Conditions" />
          <QuickLink to="/eula" icon={FileText} label="End User Licence (EULA)" />
          <QuickLink to="/privacy" icon={ShieldCheck} label="Privacy Policy" />
        </div>
      </SectionCard>

      {/* Share feedback */}
      <SectionCard
        id="share-feedback"
        title="Share your experience"
        subtitle="Optional, private by default, and never published without your permission."
        icon={Heart}
      >
        <ShareFeedbackCard />
      </SectionCard>



      {/* Security */}
      <SectionCard
        title="Security"
        subtitle="Keep your account safe and in your control."
        icon={ShieldCheck}
      >
        <div className="space-y-3">
          <div className="rounded-xl border border-border/60 bg-background px-4 py-3">
            <button
              type="button"
              onClick={() => setPwOpen((v) => !v)}
              className="flex w-full items-center justify-between gap-3 text-start"
            >
              <div className="flex items-start gap-3">
                <Lock
                  className="mt-0.5 h-5 w-5 text-[var(--gold-deep)]"
                  strokeWidth={1.5}
                />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Change password
                  </p>
                  <p className="text-xs text-muted-foreground">
                    At least 8 characters. Use something only you would know.
                  </p>
                </div>
              </div>
              <ArrowRight
                className={`h-4 w-4 text-muted-foreground transition ${pwOpen ? "rotate-90" : ""}`}
                strokeWidth={1.5}
              />
            </button>
            {pwOpen && (
              <div className="mt-4 space-y-3 border-t border-border/60 pt-4">
                <div className="relative">
                  <input
                    id="profile-new-password"
                    type={pwShow ? "text" : "password"}
                    value={pwNew}
                    onChange={(e) => setPwNew(e.target.value)}
                    placeholder="New password"
                    autoComplete="new-password"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 pe-12 text-sm focus:border-gold/60 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setPwShow((v) => !v)}
                    className="absolute end-1 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={pwShow ? "Hide password" : "Show password"}
                    aria-pressed={pwShow}
                    aria-controls="profile-new-password"
                    title={pwShow ? "Hide password" : "Show password"}
                  >
                    {pwShow ? (
                      <EyeOff aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
                    ) : (
                      <Eye aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
                    )}
                  </button>
                  <span aria-live="polite" className="sr-only">{pwShow ? "Password is visible" : "Password is hidden"}</span>
                </div>
                <div className="relative">
                  <input
                    id="profile-confirm-password"
                    type={pwShow ? "text" : "password"}
                    value={pwConfirm}
                    onChange={(e) => setPwConfirm(e.target.value)}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 pe-12 text-sm focus:border-gold/60 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setPwShow((v) => !v)}
                    className="absolute end-1 top-1/2 -translate-y-1/2 inline-flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={pwShow ? "Hide password" : "Show password"}
                    aria-pressed={pwShow}
                    aria-controls="profile-confirm-password"
                    title={pwShow ? "Hide password" : "Show password"}
                  >
                    {pwShow ? (
                      <EyeOff aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
                    ) : (
                      <Eye aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
                    )}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={changePassword}
                  disabled={pwSubmitting}
                  className="btn-gold text-sm disabled:opacity-60"
                >
                  {pwSubmitting ? "Updating…" : "Update password"}
                </button>
              </div>
            )}

          </div>

          <div className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-background px-4 py-3">
            <div className="flex items-start gap-3">
              <ShieldCheck
                className="mt-0.5 h-5 w-5 text-muted-foreground"
                strokeWidth={1.5}
              />
              <div>
                <p className="text-sm font-medium text-foreground">
                  Two-factor authentication
                </p>
                <p className="text-xs text-muted-foreground">
                  Coming soon — an extra layer of safety for your account.
                </p>
              </div>
            </div>
            <span className="rounded-full bg-muted px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Soon
            </span>
          </div>





          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4">
            <div className="flex items-start gap-3">
              <ShieldAlert
                className="mt-0.5 h-5 w-5 text-destructive"
                strokeWidth={1.5}
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">
                  Delete my account
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Permanently remove your account and the personal data we are
                  not legally required to keep. Deletion may take up to 30 days,
                  and we'll email you once it's complete.
                </p>


                {delState === "idle" && (
                  <button
                    type="button"
                    onClick={() => {
                      setDelConfirmText("");
                      setDelOpen(true);
                    }}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg border border-destructive/40 bg-background px-3 py-1.5 text-xs font-medium text-destructive transition hover:bg-destructive/10"
                  >
                    Request deletion
                  </button>
                )}
                {delState === "sending" && (
                  <p className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Submitting…
                  </p>
                )}
                {delState === "sent" && (
                  <p className="mt-3 rounded-lg bg-background px-3 py-2 text-xs text-foreground">
                    Your deletion has been started. It may take up to 30 days to
                    complete, and we'll email {email} to confirm once your
                    account and data have been removed.
                  </p>
                )}

              </div>
            </div>
          </div>

        </div>
      </SectionCard>

      <AlertDialog
        open={delOpen}
        onOpenChange={(o) => {
          if (delState === "sending") return;
          setDelOpen(o);
          if (!o) setDelConfirmText("");
        }}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <ShieldAlert className="h-5 w-5" strokeWidth={1.5} />
              Delete your account?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>
                  This permanently deletes the account for{" "}
                  <span className="font-semibold text-foreground">{email}</span>{" "}
                  and the personal data we are not legally required to keep.
                  Deletion may take up to 30 days to complete, and we'll email
                  you as soon as it's done.
                </p>

                <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs">
                  <p className="mb-1 font-semibold text-foreground">
                    What will be removed:
                  </p>
                  <ul className="list-disc space-y-1 ps-4 text-foreground/80">
                    <li>Your profile, avatar, and preferences</li>
                    <li>All journal entries, dreams, moods, and exercise sessions</li>
                    <li>Community posts, comments, hearts, and art pieces</li>
                    <li>Event reservations and saved favourites</li>
                    <li>Access to your membership and any future renewals</li>
                  </ul>
                  <p className="mt-2 text-foreground/80">
                    This cannot be undone. We recommend downloading your data
                    first.
                  </p>
                </div>
                <div>
                  <label
                    htmlFor="delete-confirm"
                    className="mb-1 block text-xs font-medium text-foreground"
                  >
                    Type <span className="font-mono font-semibold">DELETE</span>{" "}
                    to confirm
                  </label>
                  <input
                    id="delete-confirm"
                    type="text"
                    autoComplete="off"
                    value={delConfirmText}
                    onChange={(e) => setDelConfirmText(e.target.value)}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:border-destructive focus:outline-none"
                    placeholder="DELETE"
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={delState === "sending"}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={
                delConfirmText.trim() !== "DELETE" || delState === "sending"
              }
              onClick={(e) => {
                e.preventDefault();
                void submitDeletionRequest();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {delState === "sending" ? (
                <>
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                "Request deletion"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Sticky Save / Cancel */}
      <div
        aria-live="polite"
        className={`fixed inset-x-0 bottom-0 z-30 border-t border-border/60 bg-background/95 px-4 py-3 backdrop-blur transition-transform ${
          dirty || saveState === "saving" ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-3">
          <p className="hidden text-xs text-muted-foreground sm:block">
            {saveState === "saving"
              ? "Saving your changes…"
              : `${unsavedLabel} — don't forget to save.`}
          </p>
          <div className="flex flex-1 justify-end gap-2 sm:flex-none">
            <button
              type="button"
              onClick={cancelEdits}
              disabled={saveState === "saving"}
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-60"
            >
              <X className="h-4 w-4" strokeWidth={1.5} /> Cancel
            </button>
            <button
              type="button"
              onClick={saveAll}
              disabled={saveState === "saving" || !dirty || !canEdit}
              aria-busy={saveState === "saving"}
              className="btn-gold text-sm disabled:opacity-60"
            >
              {saveState === "saving" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" strokeWidth={1.5} />
                  Save changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <AvatarUploaderBoundary
        onFallback={() => {
          // Cropper crashed at render — close the modal and, if we still have
          // the picked file, upload it directly without cropping.
          const fallbackFile = pendingFile;
          setCropperOpen(false);
          setPendingFile(null);
          if (fallbackFile) void uploadAvatarBlob(fallbackFile);
        }}
      >
        <AvatarUploader
          open={cropperOpen}
          file={pendingFile}
          onClose={() => {
            setCropperOpen(false);
            setPendingFile(null);
          }}
          onUpload={uploadAvatarBlob}
        />
      </AvatarUploaderBoundary>
    </div>
  );
}

/* ---------- Reusable bits ---------- */

function SectionCard({
  id,
  title,
  subtitle,
  icon: Icon,
  children,
}: {
  id?: string;
  title?: string;
  subtitle?: string;
  icon?: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      className="mb-5 scroll-mt-24 rounded-3xl border border-border/60 bg-card p-6 shadow-[0_1px_2px_rgba(0,0,0,0.03)] md:p-7"
    >
      {title && (
        <div className="mb-5 flex items-start gap-3">
          {Icon && (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-gold/40 bg-gold-light/30 text-[var(--gold-deep)]">
              <Icon className="h-5 w-5" strokeWidth={1.5} />
            </div>
          )}
          <div>
            <h2 className="font-display text-lg font-semibold text-foreground">
              {title}
            </h2>
            {subtitle && (
              <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
            )}
          </div>
        </div>
      )}
      {children}
    </section>
  );
}

function Chip({ icon: Icon, children }: { icon: LucideIcon; children: React.ReactNode }) {
  return (
    <span className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/30 bg-gold-light/40 px-2.5 py-1 text-[11px] font-medium text-[var(--gold-deep)]">
      <Icon className="h-3 w-3" strokeWidth={1.5} />
      {children}
    </span>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  maxLength,
  type = "text",
  error,
  onBlur,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  type?: string;
  error?: string;
  onBlur?: () => void;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        className={`w-full rounded-xl border bg-background px-3 py-2.5 text-sm text-foreground transition focus:outline-none focus:ring-2 disabled:opacity-60 ${
          error
            ? "border-rose-300 focus:border-rose-400 focus:ring-rose-200/40"
            : "border-border focus:border-gold/60 focus:ring-gold/20"
        }`}
      />
      {error && (
        <p className="mt-1.5 text-xs text-rose-600/90" role="alert">
          {error}
        </p>
      )}
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  error?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        className={`w-full rounded-xl border bg-background px-3 py-2.5 text-sm text-foreground transition focus:outline-none focus:ring-2 ${
          error
            ? "border-rose-300 focus:border-rose-400 focus:ring-rose-200/40"
            : "border-border focus:border-gold/60 focus:ring-gold/20"
        }`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error && (
        <p className="mt-1.5 text-xs text-rose-600/90" role="alert">
          {error}
        </p>
      )}
    </label>
  );
}


function Toggle({
  icon: Icon,
  label,
  desc,
  value,
  onChange,
}: {
  icon?: LucideIcon;
  label: string;
  desc?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 rounded-xl border border-border/60 bg-background px-4 py-3">
      <div className="flex min-w-0 items-start gap-3">
        {Icon && (
          <Icon
            className="mt-0.5 h-5 w-5 shrink-0 text-[var(--gold-deep)]"
            strokeWidth={1.5}
          />
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{label}</p>
          {desc && <p className="mt-0.5 text-xs text-muted-foreground">{desc}</p>}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition ${
          value ? "bg-[var(--gold)]" : "bg-border"
        }`}
        aria-pressed={value}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-background shadow transition ${
            value ? "start-[22px]" : "start-0.5"
          }`}
        />
      </button>
    </label>
  );
}

// formatStreakRange now lives in @/lib/streak and is unit-tested there.




function JourneyStat({
  icon: Icon,
  label,
  value,
  to,
  hint,
  tooltip,
  iconClassName,
  iconFill,
}: {
  icon: LucideIcon;
  label: string;
  value: number | string;
  to?: string;
  hint?: string;
  tooltip?: string;
  iconClassName?: string;
  iconFill?: string;
}) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <Icon
          className={`h-4 w-4 ${iconClassName ?? "text-[var(--gold-deep)]"}`}
          strokeWidth={1.5}
          fill={iconFill}
        />
        <div className="flex items-center gap-1.5">
          {tooltip ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`About the ${label} stat`}
                    aria-expanded={tooltipOpen}
                    onClick={(e) => {
                      // Prevent the surrounding <Link> from navigating when
                      // a keyboard or pointer user activates the info button.
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                      }
                    }}
                    className="rounded-full p-1 text-muted-foreground/70 transition hover:text-[var(--gold-deep)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold-deep)] focus-visible:ring-offset-1 focus-visible:ring-offset-cream"
                  >
                    <Info className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="end"
                  sideOffset={8}
                  collisionPadding={12}
                  avoidCollisions
                  sticky="always"
                  role="tooltip"
                  className="z-[60] w-[min(20rem,calc(100vw-1.5rem))] max-w-[min(20rem,calc(100vw-1.5rem))] whitespace-normal break-words text-start text-[11px] leading-relaxed shadow-lg"
                >
                  {tooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
          {to ? (
            <ArrowRight
              className="h-3.5 w-3.5 text-muted-foreground/60 transition group-hover:translate-x-0.5 group-hover:text-[var(--gold-deep)]"
              strokeWidth={1.5}
            />
          ) : null}
        </div>
      </div>
      <div className="mt-2 font-display text-xl font-semibold text-foreground">
        {value}
      </div>
      <div className="mt-0.5 text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {hint ? (
        <div className="mt-1 text-[11px] text-muted-foreground/80">{hint}</div>
      ) : null}
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        aria-label={`View ${label}`}
        className="group block rounded-2xl border border-border/60 bg-cream/30 p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-[0_12px_30px_-18px_color-mix(in_oklab,var(--gold-deep)_45%,transparent)]"
      >
        {inner}
      </Link>
    );
  }
  return (
    <div className="rounded-2xl border border-border/60 bg-cream/30 p-4">
      {inner}
    </div>
  );
}


function QuickLink({
  to,
  icon: Icon,
  label,
  iconClassName,
  iconFill,
}: {
  to: string;
  icon: LucideIcon;
  label: string;
  iconClassName?: string;
  iconFill?: string;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-card p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-[0_12px_30px_-18px_color-mix(in_oklab,var(--gold-deep)_45%,transparent)]"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-gold/40 bg-gold-light/30 text-[var(--gold-deep)]">
          <Icon
            className={`h-[18px] w-[18px] ${iconClassName ?? ""}`}
            strokeWidth={1.5}
            fill={iconFill}
          />
        </span>
        <span className="text-sm font-medium text-foreground">{label}</span>
      </div>
      <ArrowRight
        className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-[var(--gold-deep)]"
        strokeWidth={1.5}
      />
    </Link>
  );
}


/* ---------- Admin stats (unchanged behavior) ---------- */

function AdminStatsSection() {
  const fetcher = useServerFn(getAdminOverview);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "overview", "profile"],
    queryFn: () => fetcher(),
  });

  return (
    <section className="mb-5 space-y-4 rounded-3xl border border-gold/30 bg-gradient-to-br from-cream/60 to-card p-6 md:p-7">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold text-foreground">
            Platform statistics
          </h2>
          <p className="text-xs text-muted-foreground">
            Live snapshot across members, content, and community activity.
          </p>
        </div>
        <Link
          to="/admin"
          className="hidden items-center gap-1 text-xs font-semibold text-[var(--gold-deep)] hover:underline sm:inline-flex"
        >
          Full dashboard <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
        </Link>
      </div>

      {isLoading && (
        <div className="rounded-2xl border border-gold/20 bg-card p-6 text-sm text-muted-foreground">
          <Loader2 className="me-2 inline h-4 w-4 animate-spin" />
          Loading statistics…
        </div>
      )}

      {isError && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
          Unable to load statistics right now.
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <AdminStat icon={Users} label="Total users" value={data.stats.totalUsers} />
            <AdminStat
              icon={CreditCard}
              label="Members"
              value={data.stats.totalMembers}
              hint={`+${data.stats.newMembersLast30} in 30d`}
            />
            <AdminStat icon={UserRound} label="Guests" value={data.stats.totalGuests} />
            <AdminStat
              icon={BookOpen}
              label="Journal entries"
              value={data.stats.totalJournalEntries}
            />
            <AdminStat icon={Smile} label="Mood check-ins" value={data.stats.totalMoodChecks} />
            <AdminStat
              icon={Activity}
              label="Exercise sessions"
              value={data.stats.totalExerciseSessions}
            />
            <AdminStat
              icon={MessageSquare}
              label="Community posts"
              value={data.stats.totalPosts}
            />
            <AdminStat icon={FileText} label="Comments" value={data.stats.totalComments} />
          </div>

          {data.popular.length > 0 && (
            <div className="rounded-2xl border border-gold/20 bg-card p-5">
              <div className="mb-3 flex items-center gap-2">
                <BarChart3
                  className="h-4 w-4 text-[var(--gold-deep)]"
                  strokeWidth={1.5}
                />
                <h3 className="font-display text-sm font-semibold text-foreground">
                  Most-used exercises
                </h3>
              </div>
              <div className="space-y-1.5">
                {data.popular.map((p) => (
                  <div
                    key={p.slug}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="capitalize text-foreground">
                      {p.slug.replace(/-/g, " ")}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {p.count} sessions
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function AdminStat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <div className="rounded-2xl border border-gold/20 bg-card p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-[var(--gold-deep)]" strokeWidth={1.5} />
        {label}
      </div>
      <div className="mt-1.5 font-display text-2xl font-semibold text-foreground">
        {value}
      </div>
      {hint && <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}
